import { useEffect, useState } from "react";
import { getAuditTrail } from "../../services/authService";
import { getCookie } from "../../utils/cookieUtil";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight, faSearch,faRedoAlt } from "@fortawesome/free-solid-svg-icons";


export default function AuditTrail() {
  const [auditEntries, setAuditEntries] = useState([]);
  const [loading, setDataLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [permission, setPermission] = useState('');
  const [reloadCounter, setReloadCounter] = useState(0);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchAuditTrail();
  }, [reloadCounter]);

  useEffect(() => {
    const roles = getCookie('roleId');
    if (roles) {
      setPermission(`${roles}`);
    }
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const fetchAuditTrail = async () => {
    setDataLoading(true);
    try {
      const res = await getAuditTrail();
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const converted = (res || []).map((entry) => {
        if (!entry.timestamp) {
          console.warn("Skipping entry due to missing timestamp:", entry);
          return null;
        }

        const utcDate = new Date(entry.timestamp);
        if (isNaN(utcDate.getTime())) {
          console.warn("Invalid date found:", entry.timestamp);
          return null;
        }

        const options = {
          timeZone: userTimeZone,
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        };

        const formatter = new Intl.DateTimeFormat("en-GB", options);
        const formatted = formatter.format(utcDate); // "DD/MM/YYYY, HH:MM:SS"
        const [datePart, timePart] = formatted.split(", ");

        return {
          ...entry,
          localDate: datePart,
          localTime: timePart,
          utcTime: utcDate,
        };
      });

      const validConverted = converted.filter((e) => e !== null);
      const sortedData = validConverted.sort((a, b) => b.utcTime - a.utcTime);
      setAuditEntries(sortedData);
    } catch (error) {
      console.error("Error fetching audit trail:", error);
    } finally {
      setDataLoading(false);
    }
  };

   const handleReload = () => {
    setReloadCounter(prev => prev + 1);
  };
    
   const capitalize = (str) =>
  str
    ?.split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

  const filteredEntries = auditEntries.filter((entry) => {
    const fullText = [
      entry.actionIcon,
      entry.Admin,
      entry.Action,
      entry.User,
      "on",
      entry.localDate,
      "at",
      entry.localTime,
      "via",
      entry.Platform,
    ]
      .join(" ")
      .toLowerCase();

    return fullText.includes(searchQuery.toLowerCase());
  });

  const totalPages = Math.ceil(filteredEntries.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentEntries = filteredEntries.slice(startIndex, startIndex + itemsPerPage);

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  return (
  <div className="flex-1 overflow-x-auto px-4 py-4">
    {permission.includes("adduser") && (
      <>
        <div className="flex flex-col sm:flex-row mb-4 items-center max-w-xs border border-[#900028] rounded-md focus-within:border-[#900028]">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-grow p-2 outline-none rounded-t-md sm:rounded-l-md sm:rounded-tr-none border-none"
            placeholder="Search..."
          />
          <button className="bg-[#900028] text-white px-4 py-2 rounded-b-md sm:rounded-r-md sm:rounded-bl-none">
            <FontAwesomeIcon icon={faSearch} />
          </button>
        
        </div>
          <div>
           <button
                      onClick={handleReload}
                    
                      
                      className="p-1 sm:p-2"
                      aria-label="Reload data"
                    >
                      <FontAwesomeIcon
                        icon={faRedoAlt}
                        className="text-xl sm:text-2xl text-[#900028]"
                      />
                    </button>
              </div>

        <div className="relative w-full overflow-auto">
          <table className="w-full min-w-[600px] table-fixed text-sm text-left text-gray-900">
            <thead className="bg-gray-200 text-gray-800 uppercase text-sm font-semibold">
              <tr className="h-12">
                <th className="w-[15%] px-4 py-3 text-left">Action</th>
                <th className="w-[40%] px-4 py-3 text-left">Summary</th>
                <th className="w-[15%] px-4 py-3 text-left">User</th>
                <th className="w-[15%] px-4 py-3 text-left">Date</th>
                <th className="w-[10%] px-4 py-3 text-left">Time</th>
                <th className="w-[15%] px-4 py-3 text-left">Platform</th>
              </tr>
            </thead>
            <tbody>
              {currentEntries.map((entry, index) => (
                <tr
                  key={index}
                  className="border-b hover:bg-gray-50 text-xs h-10"
                >
                  <td className="px-4 py-1 truncate">{entry.Action}</td>
                  <td className="px-4 py-1 truncate">
                    {entry.User && entry.Action && entry.Admin
                      ? `${capitalize(entry.User)} - ${entry.Action} by ${capitalize(
                          entry.Admin
                        )} (Admin user)`
                      : entry.Action && entry.User
                      ? `${capitalize(entry.User)} - ${entry.Action}`
                      : "-"}
                  </td>
                  <td className="px-4 py-1 truncate">
                    {(entry.Admin || entry.User)
                      ?.split(" ")
                      .map(
                        (word) =>
                          word.charAt(0).toUpperCase() +
                          word.slice(1).toLowerCase()
                      )
                      .join(" ")}
                  </td>

                  <td className="px-4 py-1 truncate">{entry.localDate}</td>
                  <td className="px-4 py-1 truncate">{entry.localTime}</td>
                  <td className="px-4 py-1 truncate">{entry.Platform}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-wrap justify-center items-center mt-4 gap-1">
          {(() => {
            const maxVisible = 6;
            let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
            let endPage = startPage + maxVisible - 1;

            if (endPage > totalPages) {
              endPage = totalPages;
              startPage = Math.max(1, endPage - maxVisible + 1);
            }

            const pages = [];
            for (let i = startPage; i <= endPage; i++) {
              pages.push(
                <button
                  key={i}
                  onClick={() => setCurrentPage(i)}
                  className={`px-3 py-1 text-sm rounded ${
                    currentPage === i
                      ? "font-bold text-black"
                      : "text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {i}
                </button>
              );
            }
            return pages;
          })()}

          {/* Right Arrow */}
          <button
            onClick={goToNextPage}
            disabled={currentPage === totalPages}
            className={`px-3 py-1 flex items-center ${
              currentPage === totalPages
                ? "text-gray-400 cursor-not-allowed"
                : "hover:bg-gray-100"
            }`}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      </>
    )}
  </div>
);

}

